import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  MenuItem,
  TextField,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import { MonthlyTourProgramStore } from "../../../../../store/main/MonthlyTourProgramStore";
import moment from "moment";
import { FetchUserFieldWorkChildrenType } from "../../../../../domain/models/monthly-tour-program/fetch-user-field-work-children";
import Swal from "sweetalert2";

interface AddTourProgramInlineFormProps {
  onClose: () => void;
  onSave: (data: any) => void;
  tourTypes: string[];
  selectedDate: string;
  hasMeeting: boolean; // new prop
  existingTours: { type: string }[]; // ✅ new prop
}

interface FormValues {
  date: string;
  typeOfTour: string;
  workingWith?: number;
  territory?: number;
  brick?: number;
}

const dropdownStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#226E9E" },
    "&:hover fieldset": { borderColor: "#226E9E" },
    "&.Mui-focused fieldset": { borderColor: "#226E9E" },
  },
};

interface PayloadType {
  date: string;
  hq_brick_id: string;
  hq_territory_id: string;
  type: string;
  user_id: number;
  working_with_id?: string; // optional
}

const AddTourProgramInlineForm: React.FC<AddTourProgramInlineFormProps> = ({
  selectedDate,
  onClose,
  onSave,
  tourTypes,
  hasMeeting,
  existingTours,
}) => {
  const { decodeToken, userRole, userDetails } = CustomerDatabaseStore();
  const {
    fetchUserFieldWorkChildren,
    fetchUserTerritory,
    fetchUserTerritoryBricks,
    addTourProgramForm,
    fetchHeadQuarterListTour,
  } = MonthlyTourProgramStore();

  const [workingWithOptions, setWorkingWithOptions] = useState<
    { id: number; name: string; headquarter_id: number }[]
  >([]);
  const [territoryOptions, setTerritoryOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [territoriesBricksOptions, setTerritoriesBricksOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typeError, setTypeError] = useState<string>("");

  const { control, handleSubmit, watch, reset, setValue } = useForm<FormValues>(
    {
      defaultValues: {
        date: selectedDate,
        typeOfTour: "",
        workingWith: undefined,
        territory: undefined,
        brick: undefined,
      },
    }
  );

  const typeOfTour = watch("typeOfTour");
  const workingWith = watch("workingWith");
  const territory = watch("territory");
  const brick = watch("brick");

  useEffect(() => {
    decodeToken();
  }, []);

  useEffect(() => {
    reset({ date: selectedDate });
  }, [selectedDate, reset]);

  // Fetch workingWith options when Field Work is selected
  useEffect(() => {
    if (
      typeOfTour === "Field Work" &&
      userDetails?.role_id &&
      userDetails?.id
    ) {
      const payload: { role_id: string; status: string; manager_id?: number } =
        {
          role_id: "3",
          status: "active",
        };
      if (Number(userDetails.role_id) < 5) {
        payload.manager_id = userDetails.id;
      }
      setDropdownLoading(true);
      fetchUserFieldWorkChildren(payload)
        .then((data: FetchUserFieldWorkChildrenType) => {
          const users = Array.isArray(data?.users) ? data.users : [];
          const mappedData = users.map((user: any) => ({
            id: user.id ?? 0,
            name: user.full_name || "Unknown User",
            headquarter_id: user.hq_headquarter.id || null,
          }));
          const finalOptions = [
            { id: 0, name: "Working Alone", headquarter_id: 0 },
            ...mappedData,
          ];
          setWorkingWithOptions(finalOptions);
        })
        .catch(() => setWorkingWithOptions([]))
        .finally(() => setDropdownLoading(false));
    } else {
      setWorkingWithOptions([]);
    }
  }, [
    typeOfTour,
    fetchUserFieldWorkChildren,
    userDetails,
    fetchHeadQuarterListTour,
  ]);

  // Fetch territories when Field Work
  useEffect(() => {
    if (typeOfTour === "Field Work") {
      setDropdownLoading(true);
      fetchHeadQuarterListTour()
        .then((data: any) => {
          const territories = Array.isArray(data?.headquarters)
            ? data.headquarters
            : [];
          setTerritoryOptions(
            territories.map((t: any) => ({
              id: t.id ?? 0,
              name: t.name || "Unknown Territory",
            }))
          );
        })
        .catch(() => setTerritoryOptions([]))
        .finally(() => setDropdownLoading(false));
    } else {
      setTerritoryOptions([]);
    }
  }, [typeOfTour, fetchHeadQuarterListTour]);

  // Fetch bricks when territory changes
  useEffect(() => {
    if (territory) {
      fetchUserTerritoryBricks({ headquarter_id: territory })
        .then((data: any) => {
          const bricks = Array.isArray(data?.bricks) ? data.bricks : [];
          setTerritoriesBricksOptions(
            bricks.map((b: any) => ({
              id: b.id ?? 0,
              name: b.name || "Unknown Brick",
            }))
          );
        })
        .catch(() => setTerritoriesBricksOptions([]));
    } else {
      setTerritoriesBricksOptions([]);
    }
  }, [territory, fetchUserTerritoryBricks]);

  const onSubmit = async (formValues: FormValues) => {
    if (isSubmitting) return; // prevent double-click
    // ✅ Validation
    if (typeError) return; // stop submission if error exists
    const payload = {
      tours: [
        {
          date: formValues.date,
          hq_brick_id: formValues.brick ? String(formValues.brick) : "",
          hq_territory_id: formValues.territory
            ? String(formValues.territory)
            : "",
          type: formValues.typeOfTour,
          user_id: userDetails?.id || 0,
          working_with_id:
            userDetails?.role?.name !== "HQ_MNG" &&
            formValues.workingWith &&
            formValues.workingWith !== 0
              ? String(formValues.workingWith)
              : "",
        },
      ],
    };

    setIsSubmitting(true);
    try {
      await addTourProgramForm(payload);
      onSave(payload);
      onClose();

      // ✅ SweetAlert success popup
      Swal.fire({
        icon: "success",
        title: "Tour Program Added",
        text: "Your tour program has been successfully created!",
        confirmButtonColor: "#3085d6",
      });
    } catch (err: any) {
      console.error("Error submitting tour program:", err);

      // ✅ SweetAlert error popup
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          err?.response?.data?.tours?.[0] ||
          "Something went wrong while submitting the tour program.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (typeOfTour) {
      const hasFieldWork = existingTours.some((t) => t.type === "Field Work");

      if (hasFieldWork && typeOfTour !== "Field Work") {
        setTypeError(
          "Only Field Work is allowed since Field Work already exists."
        );
      } else {
        setTypeError("");
      }
    }
  }, [typeOfTour, existingTours]);

  const minDate = moment().startOf("month").format("YYYY-MM-DD");
  const maxDate = moment().add(1, "month").endOf("month").format("YYYY-MM-DD");

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #B0B0B0",
        bgcolor: "background.paper",
        width: 300,
        boxShadow: 1,
      }}
    >
      {/* Heading */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mb={1}
        position="relative"
      >
        <Typography
          variant="subtitle2"
          align="center"
          sx={{ color: "#226E9E", fontWeight: 600 }}
        >
          Add Tour Program
        </Typography>
        <IconButton
          size="small"
          sx={{ position: "absolute", right: 0, top: 0 }}
          onClick={onClose}
        >
          <Cancel fontSize="small" sx={{ color: "#226E9E" }} />
        </IconButton>
      </Box>

      {/* Show message if meeting already exists */}
      {hasMeeting ? (
        <Box textAlign="center" p={2}>
          <Typography variant="subtitle1" color="error" mb={2}>
            A Meeting has already been scheduled for this day.
          </Typography>
          <Button variant="contained" size="small" onClick={onClose}>
            Close
          </Button>
        </Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Date Picker */}
          <Controller
            name="date"
            control={control}
            rules={{ required: "Date is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Date"
                type="date"
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                inputProps={{ readOnly: true, min: minDate, max: maxDate }}
                size="small"
                sx={dropdownStyle}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          {/* Type of Tour */}
          <Controller
            name="typeOfTour"
            control={control}
            rules={{ required: "Type of Tour is required" }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth margin="dense">
                <TextField
                  select
                  label="Type of Tour"
                  {...field}
                  size="small"
                  sx={dropdownStyle}
                  error={!!fieldState.error || !!typeError}
                  helperText={fieldState.error?.message || typeError}
                >
                  {tourTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}
          />

          {/* Territory and Brick */}
          {typeOfTour === "Field Work" && (
            <>
              <Controller
                name="territory"
                control={control}
                rules={{ required: "Territory is required for Field Work" }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth margin="dense">
                    <TextField
                      select
                      label="Territory"
                      {...field}
                      size="small"
                      sx={dropdownStyle}
                      disabled={
                        dropdownLoading || territoryOptions.length === 0
                      }
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue("brick", undefined);
                      }}
                      InputProps={{
                        endAdornment: dropdownLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              pr: 1,
                            }}
                          >
                            <CircularProgress size={20} color="inherit" />
                          </Box>
                        ) : null,
                      }}
                    >
                      {territoryOptions.length > 0
                        ? territoryOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))
                        : !dropdownLoading && (
                            <MenuItem disabled>No options</MenuItem>
                          )}
                    </TextField>
                  </FormControl>
                )}
              />

              {/* Working With */}
              {typeOfTour === "Field Work" && (
                <>
                  {userDetails.role.name !== "HQ_MNG" && (
                    <Controller
                      name="workingWith"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth margin="dense">
                          <TextField
                            select
                            label="Working With"
                            {...field}
                            size="small"
                            sx={dropdownStyle}
                            disabled={dropdownLoading}
                            error={!!error}
                            helperText={error?.message}
                            InputProps={{
                              endAdornment: dropdownLoading ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    pr: 1,
                                  }}
                                >
                                  <CircularProgress size={20} color="inherit" />
                                </Box>
                              ) : null,
                            }}
                          >
                            {workingWithOptions.length > 0
                              ? workingWithOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                  </MenuItem>
                                ))
                              : !dropdownLoading && (
                                  <MenuItem disabled>
                                    No options available
                                  </MenuItem>
                                )}
                          </TextField>
                        </FormControl>
                      )}
                    />
                  )}
                </>
              )}

              <Controller
                name="brick"
                control={control}
                rules={{
                  required: "Brick is required for Field Work",
                }}
                render={({ field, fieldState }) => {
                  const territorySelected = !!watch("territory");
                  const brickOptionsAvailable =
                    territoriesBricksOptions.length > 0;
                  const shouldDisable = dropdownLoading || !territorySelected;

                  return (
                    <FormControl fullWidth margin="dense">
                      <TextField
                        select
                        label="Brick"
                        {...field}
                        size="small"
                        sx={dropdownStyle}
                        disabled={shouldDisable}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        {brickOptionsAvailable
                          ? territoriesBricksOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))
                          : !dropdownLoading && (
                              <MenuItem disabled>No options available</MenuItem>
                            )}
                      </TextField>
                    </FormControl>
                  );
                }}
              />
            </>
          )}

          {/* Submit */}
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              type="submit"
              size="small"
              fullWidth
              sx={{ maxWidth: "100%" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default AddTourProgramInlineForm;
